/**
 * usePersonaPreferences
 * Server-backed hook for managing voice persona preferences.
 * Replaces the localStorage-based useVoiceCharacter hook.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  getCoachCharacters,
  getPersonaPreferences,
  updatePersonaPreferences,
  type CoachCharacterInfo,
  type PersonaPreferencesResponse,
  type PersonaPreferencesUpdate,
} from '@/services/settingsService';

export function usePersonaPreferences() {
  const queryClient = useQueryClient();
  // These endpoints are authenticated; firing them for anonymous visitors on
  // public pages (e.g. /legal/*) produced 401 console noise. Gate on auth.
  const { isAuthenticated } = useAuth();

  const {
    data: preferences,
    isLoading: preferencesLoading,
  } = useQuery({
    queryKey: ['persona-preferences'],
    queryFn: getPersonaPreferences,
    enabled: isAuthenticated,
  });

  const {
    data: charactersData,
    isLoading: charactersLoading,
  } = useQuery({
    queryKey: ['coach-characters'],
    queryFn: getCoachCharacters,
    staleTime: 5 * 60 * 1000, // 5 min cache — characters rarely change
    enabled: isAuthenticated,
  });

  const mutation = useMutation({
    mutationFn: updatePersonaPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona-preferences'] });
    },
  });

  const characters: CoachCharacterInfo[] = charactersData?.characters ?? [];

  return {
    preferences: preferences as PersonaPreferencesResponse | undefined,
    characters,
    archetypes: charactersData?.archetypes,
    updatePreferences: (input: PersonaPreferencesUpdate) => mutation.mutateAsync(input),
    isLoading: preferencesLoading || charactersLoading,
    /**
     * The persona-preferences query alone (#1806). A surface that only needs
     * the family's persona NAME must not wait on the character catalogue —
     * that query is cached for 5 minutes and answers a different question.
     * Anonymous visitors get `false`: the query is disabled, so it is not
     * "loading", it simply never runs.
     */
    preferencesLoading,
    isSaving: mutation.isPending,
  };
}
